import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useCallStore } from './callStore'
import { resetTwilioDeviceGlobals } from '../hooks/useTwilioDevice'
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  type CognitoUserSession,
  type CognitoUserAttribute,
} from 'amazon-cognito-identity-js'

// ========================================
// 型定義
// ========================================

interface UserTokens {
  idToken: string
  accessToken: string
}

interface AuthUser {
  email: string
  sub?: string
  name?: string
  organizationId?: string
  role?: string
  attributes?: Record<string, string>
  tokens: UserTokens
}

interface AuthStoreState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  initialized: boolean
}

interface AuthStoreActions {
  getRole: () => string
  isAdmin: () => boolean
  isOperator: () => boolean
  login: (email: string, password: string) => Promise<CognitoUserSession>
  logout: () => Promise<void>
  getCurrentUser: () => Promise<Record<string, string>>
  refreshToken: () => Promise<CognitoUserSession>
  getIdToken: () => string | undefined
  getAccessToken: () => string | undefined
  getAuthHeaders: () => Record<string, string>
  initialize: () => Promise<void>
}

type AuthStore = AuthStoreState & AuthStoreActions

// ========================================
// Cognito 設定
// ========================================

const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

// Cognito設定の警告（App.jsxでチェック済み）
if (!userPoolId || !clientId) {
  console.warn('⚠️  Cognito設定が見つかりません')
}

const poolData = {
  UserPoolId: (userPoolId || 'not-configured') as string,
  ClientId: (clientId || 'not-configured') as string,
}

let userPool: CognitoUserPool | null = null
if (userPoolId && clientId) {
  userPool = new CognitoUserPool(poolData)
}

// ========================================
// ヘルパー関数
// ========================================

const parseJwt = (token: string): Record<string, string> => {
  try {
    const [, payload] = token.split('.')
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return decoded || {}
  } catch (_) {
    return {}
  }
}

const deriveRole = (userAttributes: Record<string, string>, tokens: Partial<UserTokens>): string => {
  const attrsRole = userAttributes['custom:role'] || userAttributes.role
  if (attrsRole) return attrsRole
  const claims = tokens.idToken ? parseJwt(tokens.idToken) : {}
  return claims['custom:role'] || claims.role || ''
}

const attributesToRecord = (attributes: CognitoUserAttribute[]): Record<string, string> => {
  const record: Record<string, string> = {}
  attributes.forEach((attr) => {
    record[attr.Name] = attr.Value
  })
  return record
}

// ========================================
// Store
// ========================================

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      initialized: false,

      getRole: (): string => {
        const u = get().user
        if (!u) return ''
        if (u.role) return u.role
        const attrRole = u.attributes?.['custom:role'] || u.attributes?.role
        if (attrRole) return attrRole
        return ''
      },
      isAdmin: () => get().getRole() === 'admin',
      isOperator: () => get().getRole() === 'operator',

      login: async (email: string, password: string): Promise<CognitoUserSession> => {
        set({ isLoading: true, error: null })

        const authenticationDetails = new AuthenticationDetails({
          Username: email,
          Password: password,
        })

        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: userPool!,
        })

        return new Promise((resolve, reject) => {
          cognitoUser.authenticateUser(authenticationDetails, {
            onSuccess: (result: CognitoUserSession) => {
              const idToken = result.getIdToken().getJwtToken()
              const accessToken = result.getAccessToken().getJwtToken()
              const tokens: UserTokens = { idToken, accessToken }

              // ユーザー属性を取得
              cognitoUser.getUserAttributes((err, attributes) => {
                if (err || !attributes) {
                  console.error('ユーザー属性の取得に失敗:', err)
                  // 属性取得失敗でもログインは成功とする
                  set({
                    user: {
                      email,
                      tokens,
                    },
                    isAuthenticated: true,
                    isLoading: false,
                  })
                  resolve(result)
                  return
                }

                // 属性をオブジェクトに変換
                const userAttributes = attributesToRecord(attributes)

                const organizationId = userAttributes['custom:organizationId']
                if (!organizationId) {
                  const attrErr = new Error('組織IDを取得できませんでした。管理者にお問い合わせください。')
                  cognitoUser.signOut()
                  set({
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                    error: attrErr.message,
                  })
                  reject(attrErr)
                  return
                }

                set({
                  user: {
                    email,
                    sub: userAttributes.sub,
                    name: userAttributes.name,
                    organizationId,
                    role: deriveRole(userAttributes, tokens),
                    attributes: userAttributes,
                    tokens,
                  },
                  isAuthenticated: true,
                  isLoading: false,
                })

                resolve(result)
              })
            },
            onFailure: (err) => {
              set({
                error: err.message || 'ログインに失敗しました',
                isLoading: false,
              })
              reject(err)
            },
          })
        })
      },

      logout: async () => {
        const state = get()
        const accessToken = state.user?.tokens?.accessToken

        const cognitoUser = userPool?.getCurrentUser()
        if (cognitoUser) {
          cognitoUser.signOut()
        }

        // presenceをオフラインに（ベストエフォート）
        if (accessToken) {
          try {
            await fetch(`${API_BASE_URL}/api/presence/offline`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${accessToken}` },
            })
          } catch (_) {
            // no-op
          }
        }

        // Twilio Device を破棄し、通話状態をリセット
        await useCallStore.getState().reset()
        resetTwilioDeviceGlobals()

        set({
          user: null,
          isAuthenticated: false,
          error: null,
        })
      },

      getCurrentUser: (): Promise<Record<string, string>> => {
        return new Promise((resolve, reject) => {
          const cognitoUser = userPool?.getCurrentUser()

          if (!cognitoUser) {
            reject(new Error('ユーザーが見つかりません'))
            return
          }

            cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
              if (err || !session) {
                reject(err || new Error('セッション取得に失敗しました'))
                return
              }

            if (!session.isValid()) {
              reject(new Error('セッションが無効です'))
              return
            }

              cognitoUser.getUserAttributes((err, attributes) => {
                if (err || !attributes) {
                  reject(err || new Error('属性取得に失敗しました'))
                  return
                }

              const userAttributes = attributesToRecord(attributes)

              const organizationId = userAttributes['custom:organizationId']
              if (!organizationId) {
                const error = new Error('組織IDを取得できませんでした。再度ログインしてください。')
                cognitoUser.signOut()
                set({
                  user: null,
                  isAuthenticated: false,
                  error: error.message,
                })
                reject(error)
                return
              }

              set({
                user: {
                  email: userAttributes.email,
                  sub: userAttributes.sub,
                  name: userAttributes.name,
                  organizationId,
                  role: deriveRole(userAttributes, {
                    idToken: session.getIdToken().getJwtToken(),
                    accessToken: session.getAccessToken().getJwtToken(),
                  }),
                  attributes: userAttributes,
                  tokens: {
                    idToken: session.getIdToken().getJwtToken(),
                    accessToken: session.getAccessToken().getJwtToken(),
                  },
                },
                isAuthenticated: true,
              })

              resolve(userAttributes)
            })
          })
        })
      },

      // トークンをリフレッシュ
      refreshToken: async (): Promise<CognitoUserSession> => {
        return new Promise((resolve, reject) => {
          const cognitoUser = userPool?.getCurrentUser()

          if (!cognitoUser) {
            reject(new Error('ユーザーが見つかりません'))
            return
          }

          cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
            if (err || !session) {
              console.error('セッション取得エラー:', err)
              // ネットワークエラーの場合はその旨を伝える（ログアウト判定に使う）
              const isNetworkError = !navigator.onLine ||
                (err instanceof Error && /network|fetch|ECONNREFUSED/i.test(err.message))
              reject(isNetworkError
                ? new Error('Network error')
                : (err || new Error('セッション取得に失敗しました'))
              )
              return
            }

            if (!session.isValid()) {
              console.warn('セッションが無効です。ログアウトします。')
              get().logout()
              reject(new Error('セッションが無効です'))
              return
            }

            // セッションが有効な場合、トークンを更新
            cognitoUser.getUserAttributes((err, attributes) => {
              if (err || !attributes) {
                console.error('ユーザー属性取得エラー:', err)
                reject(err || new Error('属性取得に失敗しました'))
                return
              }

              const userAttributes = attributesToRecord(attributes)

              const organizationId = userAttributes['custom:organizationId']
              if (!organizationId) {
                const error = new Error('組織IDを取得できませんでした')
                get().logout()
                reject(error)
                return
              }

              // ストアを更新
              set({
                user: {
                  email: userAttributes.email,
                  sub: userAttributes.sub,
                  name: userAttributes.name,
                  organizationId,
                  role: deriveRole(userAttributes, {
                    idToken: session.getIdToken().getJwtToken(),
                    accessToken: session.getAccessToken().getJwtToken(),
                  }),
                  attributes: userAttributes,
                  tokens: {
                    idToken: session.getIdToken().getJwtToken(),
                    accessToken: session.getAccessToken().getJwtToken(),
                  },
                },
                isAuthenticated: true,
              })

              resolve(session)
            })
          })
        })
      },

      getIdToken: (): string | undefined => {
        return get().user?.tokens?.idToken
      },

      getAccessToken: (): string | undefined => {
        return get().user?.tokens?.accessToken
      },

      getAuthHeaders: (): Record<string, string> => {
        const token = get().user?.tokens?.accessToken
        if (!token) {
          return {}
        }
        return {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      },

      // 起動時にローカルのセッションからトークンを更新
      initialize: async () => {
        if (get().initialized) return
        try {
          const cognitoUser = userPool?.getCurrentUser()
          if (!cognitoUser) {
            // Cognito SDK にセッションがない場合、persist から復元された認証状態をクリア
            if (get().isAuthenticated) {
              console.warn('Cognitoセッションが見つかりません。認証状態をクリアします。')
              set({ user: null, isAuthenticated: false })
            }
            set({ initialized: true })
            return
          }

          await get().getCurrentUser()
        } catch (err) {
          console.warn('セッション更新に失敗しました。ログアウトします。', err)
          get().logout()
        } finally {
          set({ initialized: true })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
